import LoadingDirective from '@/components/Directives/Loading';
import HttpClient from '@/components/Plugins/HttpClient';
import { all } from '@awesome.me/kit-d3c0f9ff86/icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { createHead } from '@unhead/vue/client';
import vClickOutside from 'click-outside-vue3';
import { createPinia, setActivePinia } from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import { createApp } from 'vue';
import { getBugsnagVuePlugin } from './bugsnag';
import LoginFormComponent from './components/auth/LoginFormComponent.vue';
import EarlyDepositLogin from './components/Views/Clients/Payments/EarlyDepositLogin.vue';
import { getLaunchDarklyVuePlugin } from './launchdarkly';
import EmployeePortalApp from './spa/EmployeePortalApp.vue';
import { buildCompanyPortalRouter, buildEmployeePortalRouter } from './spa/router';
import * as SpaModule from './vue-app-spa';
import { installPrimeVue } from './vue-app-spa';

library.add(...all);

const modules = {
    'login-form': LoginFormComponent,
    'early-deposit-login': EarlyDepositLogin,
};

const vueExtensions = [vClickOutside, HttpClient, createHead(), getBugsnagVuePlugin()];

/** Critical: pinia can only be created once to avoid messy multiple instances of the same named stores */
const pinia = createPinia();
pinia.use(piniaPluginPersistedState);
setActivePinia(pinia);

/**
 * @param {JQuery.Selector} rootElementSelector
 * @param {Parameters<typeof createApp>[1]} props
 * @param {Record<import('vue').InjectionKey, Symbol>} provide
 */
export const initEmployeePortal = (rootElementSelector, props = {}, provide = {}) => {
    const app = createApp(EmployeePortalApp, props);

    // app.config.compilerOptions.whitespace = 'preserve';

    SpaModule.decorate(app, true);

    app.component('FontAwesomeIcon', FontAwesomeIcon);
    app.use(buildEmployeePortalRouter('/'));
    app.use(getBugsnagVuePlugin());

    Object.getOwnPropertySymbols(provide).forEach(injectionKey => {
        app.provide(injectionKey, provide[injectionKey]);
    });

    app.mount(rootElementSelector);

    return { app };
};

/**
 * @param {JQuery.Selector} rootElementSelector
 * @param {Record<import('vue').InjectionKey, Symbol>} provide
 */
export const initVueSprinkle = (rootElementSelector, provide = {}) => {
    const app = createApp({});
    SpaModule.decorate(app, true);
    app.use(buildCompanyPortalRouter('/'));

    app.config.globalProperties.$log = console.log;
    app.config.unwrapInjectedRef = true;

    Object.getOwnPropertySymbols(provide).forEach(injectionKey => {
        app.provide(injectionKey, provide[injectionKey]);
    });

    for (const extension of vueExtensions) {
        app.use(extension);
    }
    app.mount(rootElementSelector);

    return { app };
};

/**
 * @param {JQuery.Selector} elementId
 * @param {{props: Parameters<typeof createApp>[1]}} createAppOptions
 */
export const initVueComponent = async (elementId, { props } = {}) => {
    const module = (await import(`./vue-${elementId}.js`)).default;

    // The layout initializes roots (e.g. navigation-app) that not every blade renders.
    if (!document.querySelector(module.element)) {
        return;
    }

    const app = createApp(module.component, props);

    app.config.globalProperties.$log = console.log;

    installPrimeVue(app);

    app.component('font-awesome-icon', FontAwesomeIcon);
    for (const extension of vueExtensions) {
        app.use(extension);
    }
    app.use(getLaunchDarklyVuePlugin());

    app.use(pinia);
    app.directive('loading', LoadingDirective);
    app.mount(module.element);

    return app;
};

/** @param {string} componentName */
export const loadComponent = componentName => {
    const component = modules[componentName];
    const app = createApp({});
    const tagNameInMarkup = componentName;
    app.component(tagNameInMarkup, component);
    app.component('font-awesome-icon', FontAwesomeIcon);

    app.config.globalProperties.$log = console.log;

    for (const extension of vueExtensions) {
        app.use(extension);
    }
    app.use(getLaunchDarklyVuePlugin());

    app.mount('#app');

    return app;
};
